import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  ArrowUpRight,
  Bot,
  Check as CheckIcon,
  ChevronDown,
  FileText,
  Factory,
  Hammer,
  Home,
  MapPin,
  Scale,
  Search,
  TrendingUp,
  Wind,
} from "lucide-react";
import Seo from "@/components/Seo";
import SampleMonthlyReport from "@/components/SampleMonthlyReport";
import WhatYouGet from "@/components/WhatYouGet";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const INDUSTRIES = [
  { icon: Wind, label: "HVAC" },
  { icon: Home, label: "Real Estate" },
  { icon: Scale, label: "Legal" },
  { icon: Hammer, label: "Home Services" },
  { icon: Factory, label: "Manufacturing" },
];

const WHY = [
  {
    n: "1",
    title: "They're asking AI, not just Google",
    copy: "More and more buyers open ChatGPT, Gemini or Claude and ask who the best option in their city is — then go with the names they get back.",
  },
  {
    n: "2",
    title: "Know where your business stands.",
    copy: "The campaign tracks whether your business appears for the searches we agree on, and pinpoints exactly where your visibility needs improvement.",
  },
  {
    n: "3",
    title: "Start building your AI visibility now",
    copy: "Find out where your business stands in AI search, then start working toward better visibility for the services and locations that matter to you. Our AI Visibility Program handles content creation, authority building, and monthly tracking—so you can take action without figuring it all out yourself.",
  },
];

const HOW = [
  {
    icon: Search,
    title: "We read your whole site",
    copy: "We point crawlers across your website so Google and the AI engines can read and understand everything you offer.",
  },
  {
    icon: FileText,
    title: "We build your content",
    copy: "Written, audio and visual content published across the web, so the AI keeps seeing you as the answer in your niche.",
  },
  {
    icon: TrendingUp,
    title: "We grow your authority",
    copy: "No promised Google ranking improvement — most clients see it anyway as a byproduct bonus of the authority work.",
  },
];

const PROOF = [
  {
    title: "Top-20 city rankings, every month",
    copy: "Your city plus the four nearest. See where you sit on Google and in AI answers, and how you're moving.",
  },
  {
    title: "Before-and-after snapshots",
    copy: "Dated, repeatable proof of what each platform — Google, ChatGPT, Gemini, Claude — says about your business.",
  },
  {
    title: "Content we actually publish",
    copy: "Every post, video and article appears in your dashboard with its link. Nothing behind a curtain.",
  },
];

const TIMELINE = [
  {
    when: "Week 1",
    what: "Setup",
    copy: "We lock in your target searches, point the crawlers at your site, make your pages easy for AI to read and cite, and turn on your dashboard.",
  },
  {
    when: "Months 1–2",
    what: "Build the base",
    copy: "Citable content and authority building start going out. The AI engines begin reading and citing you more.",
  },
  {
    when: "Months 3–5",
    what: "Climb",
    copy: "Your presence compounds. You watch your AI mentions and visibility move on the dashboard each month.",
  },
  {
    when: "Month 6",
    what: "The checkpoint",
    copy: "We review exactly where you stand together. Named in the AI answers? Done. Not yet? Billing stops and we keep going free until you are.",
  },
];

const FAQS = [
  {
    q: "What exactly is included in $397/mo?",
    a: "Pure AI Engine optimization: crawler access, published unique content and authority building placement so ChatGPT, Gemini and Claude name you. It works great on its own — and layers perfectly on our full SEO campaigns. Everything shows up in your monthly dashboard.",
  },
  {
    q: "How is this different from regular SEO?",
    a: "Regular SEO gets you ranked on Google. This is the new discipline: making AI engines recommend you when people ask who the best option is. It works great alongside our full SEO campaigns — the authority work helps each side.",
  },
  {
    q: "Which AI platforms do you track?",
    a: "ChatGPT, Gemini and Claude — the three your customers are actually asking. Your dashboard shows how often each one names your business for your target searches, month over month.",
  },
  {
    q: "How fast will I see movement?",
    a: "AI mentions often start moving within weeks — the field is wide open. You'll see every movement on the dashboard from month one, dated, so nothing is taken on faith.",
  },
  {
    q: "What if you miss the top 10 in 6 months?",
    a: "Your billing stops and we keep doing the work at our own cost until a report shows you in the top 10. You don't pay for a result we didn't get you.",
  },
  {
    q: "Can I cancel?",
    a: "Yes. Month-to-month, no long contract. We're keep clients by getting results, not by locking doors.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function Check() {
  return (
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold text-[#3A2A00]">
      <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
    </span>
  );
}

function FaqRow({ item, open, onToggle, idx }) {
  return (
    <div
      data-testid={`ai-faq-item-${idx}`}
      className="rounded-2xl border border-slate-200 bg-white"
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-semibold text-slate-900">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-flame transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="px-6 pb-6 text-lg leading-relaxed text-mist">{item.a}</p>
      )}
    </div>
  );
}

export default function AiVisibilityPage() {
  const [faq, setFaq] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdTraffic = new URLSearchParams(location.search).has("ad");
  const goBook = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };
  const offerSchema = {
    "@context": "https://schema.org",
    "@type": "Offer",
    name: "SEO + AI Visibility Program — Founding Client Rate",
    price: "397",
    priceCurrency: "USD",
    availability: "https://schema.org/LimitedAvailability",
    url: "https://www.zddesignagency.com/ai-visibility",
  };

  return (
    <main data-testid="ai-visibility-page" className={isAdTraffic ? "pb-24" : ""}>
      <Seo
        title="SEO + AI Visibility — $397/mo Founding Rate | ZD Design"
        description="AI Engine optimization: everything that decides whether ChatGPT, Gemini and Claude name your business. Works great alongside full SEO campaigns. $397/mo founding rate, 6-month guarantee."
        path="/ai-visibility"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(offerSchema)}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="grain relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-gold/20 blur-[140px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:px-12">
          <FadeUp>
            <span
              data-testid="ai-hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-1.5 font-mono text-[12px] uppercase tracking-[0.2em] text-faint"
            >
              AI Visibility Program for small businesses
            </span>
            <h1 className="mt-6 max-w-xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              You're found on Google. Now get{" "}
              <span className="relative inline-block text-slate-900">
                <span className="relative z-10">found in AI.</span>
                <span
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-1 z-0 h-4 -rotate-1 rounded-sm bg-gold"
                />
              </span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-mist md:text-xl">
              ZD Design's managed AI Visibility Program helps your business
              work toward greater visibility in answers from ChatGPT, Gemini,
              and Claude. We handle content creation, authority building, and
              monthly tracking so you can focus on running your business.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={goBook}
                data-testid="ai-hero-cta"
                className="group inline-flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
              >
                Book a Free AI Visibility Call
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                href="#ai-included"
                data-testid="ai-hero-secondary"
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame hover:text-flame"
              >
                What's included in the AI Visibility Program
              </a>
            </div>
            <p className="mt-6 text-base text-faint">
              <strong className="text-slate-900">$397/month</strong> — Content
              creation • Authority building • Monthly visibility dashboard
            </p>
            <p className="mt-2 max-w-lg text-base text-faint">
              Talk with Todd about your business, the searches you want to
              appear for, and whether this program is a fit.
            </p>
            <p className="mt-2 text-base text-faint">
              Already investing in SEO? This program adds a focused AI
              visibility service alongside it.
            </p>
          </FadeUp>

          <FadeUp delay={0.12}>
            <div className="relative">
              <div data-testid="ai-hero-photo" className="overflow-hidden rounded-tr-[2.5rem]">
                <img
                  src="/img/todd-line-art.webp"
                  alt="Line drawing of Todd Meisler leaning against a brick wall"
                  className="w-full"
                />
              </div>
              <div className="absolute -bottom-5 left-6 flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/95 px-5 py-3 shadow-lg backdrop-blur">
                <MapPin className="h-5 w-5 text-flame" />
                <span className="font-mono text-[13px] uppercase tracking-[0.15em] text-slate-900">
                  Named by AI · Your business, your city
                </span>
              </div>
              <div className="absolute -top-4 right-6 rounded-full bg-gold px-4 py-2 font-mono text-[12px] font-semibold uppercase tracking-[0.15em] text-[#3A2A00] shadow-md">
                Founding rate inside
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* INDUSTRY STRIP */}
      <section
        data-testid="ai-industry-strip"
        className="border-y border-slate-200 bg-obsidian py-7"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-12 gap-y-4 px-4 sm:px-8">
          {INDUSTRIES.map((i) => (
            <span
              key={i.label}
              className="flex items-center gap-2.5 font-mono text-[14px] font-semibold uppercase tracking-[0.18em] text-faint"
            >
              <i.icon className="h-5 w-5 text-flame" />
              {i.label}
            </span>
          ))}
        </div>
      </section>

      {/* WHY NOW */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Why this matters now</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              Search is moving. Your customers are moving with it.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {WHY.map((w, i) => (
              <FadeUp key={w.n} delay={i * 0.08}>
                <div className="font-display text-6xl font-extrabold text-flame/25">
                  {w.n}
                </div>
                <h3 className="mt-3 text-xl font-bold text-slate-900">
                  {w.title}
                </h3>
                <p className="mt-2 text-lg leading-relaxed text-mist">
                  {w.copy}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      
      {/* WHAT WE DO */}
      <section
        id="ai-included"
        className="border-y border-slate-200 bg-ink py-20 md:py-24"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>What we actually do</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              All AI search engines. One program.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {HOW.map((h, i) => (
              <FadeUp key={h.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-obsidian p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,15,25,0.08)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/15 text-[#8A5B00]">
                    <h.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 text-xl font-bold text-slate-900">
                    {h.title}
                  </h3>
                  <p className="mt-2 text-lg leading-relaxed text-mist">
                    {h.copy}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-gold/40 bg-gold/10 px-7 py-5">
              <Bot className="h-6 w-6 shrink-0 text-flame" />
              <p className="text-lg text-slate-900">
                <strong>Watch it all on a live dashboard</strong> — how often
                ChatGPT, Gemini and Claude name you, updated every month.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

            
            
      {/* PROOF — DARK */}
      <section className="relative overflow-hidden bg-[#0B0F19] py-20 text-slate-100 md:py-24">
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <FadeUp>
              <Eyebrow>Reporting</Eyebrow>
              <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl">
                See Your Progress Each Month.
              </h2>
              <ul className="mt-10 space-y-7">
                {PROOF.map((p) => (
                  <li key={p.title} className="flex gap-4">
                    <Check />
                    <span>
                      <span className="block font-bold text-slate-50">
                        {p.title}
                      </span>
                      <span className="mt-1 block leading-relaxed text-slate-400">
                        {p.copy}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </FadeUp>

            <FadeUp delay={0.12}>
              <SampleMonthlyReport testid="ai-sample-report" />
            </FadeUp>
          </div>

          <FadeUp delay={0.15}>
            <div
              data-testid="ai-real-result"
              className="mt-14 border-t border-white/10 pt-10"
            >
              <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-gold">
                Real result
              </div>
              <p className="mt-3 max-w-3xl font-display text-2xl font-bold leading-snug sm:text-3xl">
                Lee Summit Insurance Advisors — started the GEO program and
                within 3 weeks had 3 calls from ChatGPT.
              </p>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div>
                  <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-gold">
                    Why it worked
                  </div>
                  <p className="mt-2 text-xl font-bold">
                    Optimized website
                  </p>
                  <p className="mt-1 text-lg text-slate-400">
                    Made it easy to get the leads in.
                  </p>
                </div>
                <div>
                  <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-gold">
                    Climbing now
                  </div>
                  <p className="mt-2 text-xl font-bold">
                    DLM Commercial Real Estate
                  </p>
                  <p className="mt-1 text-lg text-slate-400">
                    4 inquiries from organic search in 30 days, website 100%
                    optimized in 45.
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>The guarantee</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              Top 10 in AI search within 6 months — or you stop paying.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <FadeUp>
              <div className="h-full rounded-2xl border border-slate-200 bg-obsidian p-8">
                <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                  Recommended campaign: 6 months
                </div>
                <p className="mt-3 text-xl leading-relaxed text-slate-900">
                  We recommend six months to build your AI visibility and
                  measure progress.
                </p>
                <ul className="mt-5 space-y-3">
                  {[
                    "Citable pages published for your services and locations",
                    "AI crawler access opened and verified",
                    "Authority building — mentions, reviews and citations across the web",
                    "Monthly report — where you stand, month over month",
                  ].map((d, i) => (
                    <li
                      key={d}
                      data-testid={`deliverable-${i}`}
                      className="flex items-start gap-3"
                    >
                      <Check className="mt-1 h-4 w-4 shrink-0 text-flame" strokeWidth={3} />
                      <span className="text-lg leading-relaxed text-slate-900">
                        {d}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.08}>
              <div className="h-full rounded-2xl border border-gold/50 bg-gold/10 p-8">
                <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-[#8A5B00]">
                  Our six-month guarantee
                </div>
                <p className="mt-3 text-xl leading-relaxed text-slate-900">
                  Commit to and complete six paid months. If your business
                  hasn't reached the top 10 for your agreed target search by
                  the end of month six, as measured in your campaign
                  dashboard, your billing stops—and we continue working at no
                  additional charge until you reach that target.
                </p>
                <p className="mt-4 text-base leading-relaxed text-faint">
                  Clients who end the campaign before completing six paid
                  months do not qualify for continued work at no charge.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="border-y border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>The 6 months</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              Set up once. We run it from there.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-6 md:grid-cols-4">
            {TIMELINE.map((t, i) => (
              <FadeUp key={t.when} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-slate-200 bg-obsidian p-7">
                  <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                    {t.when}
                  </div>
                  <h3 className="mt-2 text-xl font-bold text-slate-900">
                    {t.what}
                  </h3>
                  <p className="mt-2 text-base leading-relaxed text-mist">
                    {t.copy}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

            
      {/* PRICING */}
      <WhatYouGet />
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>What it costs</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              One simple rate. The risk is ours.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              AI Engine optimization — everything that decides whether
              ChatGPT, Gemini and Claude name your business. It works great
              alongside our full SEO campaigns.
            </p>
          </FadeUp>

          <div className="mt-12 grid items-start gap-8 lg:grid-cols-2">
            <FadeUp>
              <div
                data-testid="ai-pricing-photo"
                className="relative h-full overflow-hidden rounded-tr-[2.5rem]"
              >
                <img
                  src="/img/todd-line-art-2.webp"
                  alt="Line drawing of Todd Meisler, founder and chief strategist at ZD Design"
                  loading="lazy"
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute bottom-5 left-5 rounded-2xl bg-[#0B0F19]/85 px-5 py-3 font-mono text-[13px] uppercase tracking-[0.15em] text-gold backdrop-blur">
                  Todd Meisler — Founder &amp; strategist
                </div>
              </div>
            </FadeUp>

            <div className="space-y-8">
            <FadeUp delay={0.08}>
              <div
                data-testid="ai-pricing-card"
                className="rounded-3xl border border-gold/50 bg-gold/15 p-9"
              >
                <p className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  All AI engines. One flat monthly. Cancel anytime.
                </p>
                <div className="mt-4 flex flex-wrap items-end gap-4">
                  <span className="font-display text-2xl font-bold text-faint line-through decoration-flame/70 decoration-2">
                    $597
                  </span>
                  <span
                    data-testid="ai-price"
                    className="font-display text-7xl font-extrabold leading-none tracking-tight text-slate-900"
                  >
                    $397
                    <span className="text-2xl font-bold text-faint">/mo</span>
                  </span>
                </div>
                <div className="mt-3 font-mono text-[13px] uppercase tracking-[0.2em] text-[#8A5B00]">
                  Founding rate — locked while you stay
                </div>
                <ul className="mt-7 space-y-3 text-lg text-slate-900">
                  {[
                    "AI Engine optimization: crawlers, citable pages, structured data, authority building for ChatGPT, Gemini and Claude",
                    "Live dashboard — AI mentions and visibility, updated monthly",
                    "Six-month campaign recommended. Top-10 guarantee requires completion of six paid months.",
                  ].map((b) => (
                    <li key={b} className="flex gap-3">
                      <Check />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="rounded-3xl border border-slate-200 bg-obsidian p-9">
                <h3 className="font-display text-2xl font-bold text-slate-900">
                  After you rank
                </h3>
                <p className="mt-3 text-lg leading-relaxed text-mist">
                  Getting to the top 10 is step one — staying there is ongoing,
                  because competitors and the AI keep moving. Continue
                  month-to-month at the same founding rate to hold your spot
                  and keep the content flowing.
                </p>
                <p className="mt-4 text-lg leading-relaxed text-mist">
                  No long contract. Cancel anytime. We keep clients by getting
                  results, not by locking doors.
                </p>
                <Link
                  to="/contact"
                  data-testid="ai-price-cta"
                  className="group mt-7 inline-flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
                >
                  Claim the founding rate
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <p className="mt-4 text-base text-faint">
                  No credit card to start the conversation · 30 minutes with
                  Todd
                </p>
              </div>
            </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <FadeUp>
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              Fair questions.
            </h2>
          </FadeUp>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.05}>
                <FaqRow
                  item={f}
                  idx={i}
                  open={faq === i}
                  onToggle={() => setFaq(faq === i ? -1 : i)}
                />
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1}>
            <p className="mt-8 text-lg text-faint">
              More questions?{" "}
              <Link
                to="/faq"
                className="font-semibold text-flame underline underline-offset-4 hover:text-ember"
              >
                All 29 answers live here
              </Link>
              <ArrowUpRight className="inline h-4 w-4" />
            </p>
          </FadeUp>
        </div>
      </section>

      {/* FINAL CTA — DARK */}
      <section className="relative overflow-hidden bg-[#0B0F19] py-20 text-center text-slate-100 md:py-24">
        <div className="relative mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <h2 className="font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-5xl">
              You were early on Google.{" "}
              <span className="text-gold">Be early on AI too.</span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-400">
              AI keeps naming the sources it has seen longest. The businesses
              that start now are the ones it keeps recommending later.
            </p>
            <button
              type="button"
              onClick={goBook}
              data-testid="ai-final-cta"
              className="group mt-9 inline-flex items-center gap-2 rounded-2xl bg-flame px-8 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_32px_rgba(255,85,0,0.45)]"
            >
              Book a free call with Todd
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </FadeUp>
        </div>
      </section>

      {/* AD-TRAFFIC CONVERSION BAR */}
      {isAdTraffic && (
        <div
          data-testid="ai-ad-bar"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[#0B0F19]/95 px-4 py-3 backdrop-blur"
        >
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-4">
            <div className="text-left">
              <div className="font-mono text-[12px] uppercase tracking-[0.2em] text-gold">
                SEO + AI Visibility · Founding rate
              </div>
              <div className="font-display text-xl font-extrabold leading-tight text-white">
                $397/mo{" "}
                <span className="text-sm font-semibold text-slate-400 line-through">
                  $597
                </span>
              </div>
            </div>
            <Link
              to="/contact"
              data-testid="ai-ad-bar-cta"
              className="group inline-flex shrink-0 items-center gap-2 rounded-2xl bg-flame px-6 py-3 font-mono text-sm font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)]"
            >
              Claim your spot
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      )}
    </main>
  );
}
